import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import "./ManageTeamLeave.scss";
import InputField from "../../components/InputComponent/InputField";
import TeamLeaveReport from "./TeamLeaveReport";
import LeaveBalanceReport from "./LeaveBalanceReport";
import "react-toastify/dist/ReactToastify.css";
import GeneralTable from "../../components/Table/GeneralTable";
import IconButton from "../../components/IconButton/IconButton";
import StatusCard from "../../components/StatusCard/StatusCard";
import { useAuth } from "../../contexts/AuthContext";

function LeaveReport({ onRequestProcessed }) {
  const { user } = useAuth();
  const [leaveTypeFilter, setLeaveTypeFilter] = useState("All");
  const [leaveStatusFilter, setLeaveStatusFilter] = useState("All");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [data, setData] = useState([]);
  const [viewReport, setViewReport] = useState(false);
  const [viewSummary, setViewSummary] = useState(false);
  const [activeRole, setActiveRole] = useState(null);
  const [selectedLeaves, setSelectedLeaves] = useState(new Set());

  // Determine available roles
  const availableRoles = React.useMemo(() => {
    if (!user || !user.authorities) return [];

    const roles = [];

    if (user.authorities.includes("ROLE_ADMIN")) roles.push("ADMIN");
    if (user.authorities.includes("ROLE_HR")) roles.push("HR");
    if (user.authorities.includes("ROLE_MANAGER")) roles.push("MANAGER");

    return roles;
  }, [user]);

  // Set initial active role
  useEffect(() => {
    if (availableRoles.length > 0 && !activeRole) {
      const defaultRole = availableRoles.includes("MANAGER")
        ? "MANAGER"
        : availableRoles[0];
      setActiveRole(defaultRole);
    }
  }, [availableRoles, activeRole]);

  const showRoleButtons = availableRoles.length > 1;

  // Helper function to map display status to backend status
  const mapStatusToBackend = (displayStatus) => {
    const statusMapping = {
      All: undefined,
      Requests: "REQUESTED",
      "Revoke Requests": "REVOKE",
    };
    return statusMapping[displayStatus];
  };

  // Get API endpoint based on active role
  const getApiEndpoint = () => {
    const roleEndpoints = {
      ADMIN: `${process.env.REACT_APP_API_BASE_URL}/leave-history/pending`,
      MANAGER: `${process.env.REACT_APP_API_MANAGER_URL}/leave-history/pending`,
      HR: `${process.env.REACT_APP_API_HR_URL}/leave-history/pending`,
    };
    return roleEndpoints[activeRole];
  };

  // Check if current role can perform actions
  const canPerformActions = () => {
    return activeRole === "MANAGER" || activeRole === "HR";
  };

  useEffect(() => {
    if (!activeRole) return;

    const fetchLeaveHistory = async () => {
      try {
        const backendStatus = mapStatusToBackend(leaveStatusFilter);
        const endpoint = getApiEndpoint();

        console.log("activeRole:", activeRole);
        console.log("endpoint:", endpoint);

        const response = await axiosInstance.get(endpoint, {
          params: {
            leaveType: leaveTypeFilter === "All" ? undefined : leaveTypeFilter,
            leaveStatus: backendStatus,
            startDate: startDateFilter,
            endDate: endDateFilter,
          },
        });

        // Sort data by date (latest first)
        const sortedData = (response.data.data || []).sort((a, b) => {
          const dateA =
            a.dates && a.dates.length > 0 ? new Date(a.dates[0]) : new Date(0);
          const dateB =
            b.dates && b.dates.length > 0 ? new Date(b.dates[0]) : new Date(0);
          return dateB - dateA; // Latest first
        });

        setData(sortedData);
        setSelectedLeaves(new Set()); // Clear selection when data changes
      } catch (error) {
        console.error(
          "Error fetching leave history:",
          error.response || error.message,
        );
        setData([]);
      }
    };

    fetchLeaveHistory();
  }, [
    activeRole,
    leaveTypeFilter,
    leaveStatusFilter,
    startDateFilter,
    endDateFilter,
  ]);

  const handleRoleChange = (role) => {
    setActiveRole(role);
    setSelectedLeaves(new Set());
  };

  const filteredData = (data || []).filter((item) => {
    const matchesLeaveType =
      leaveTypeFilter === "All" ||
      (Array.isArray(item.leaveTypes) &&
        item.leaveTypes.includes(leaveTypeFilter));

    const backendStatusFilter = mapStatusToBackend(leaveStatusFilter);
    const matchesLeaveStatus =
      leaveStatusFilter === "All" || item.leaveStatus === backendStatusFilter;

    const matchesDateRange =
      (startDateFilter === "" ||
        new Date(item.dates[0]) >= new Date(startDateFilter)) &&
      (endDateFilter === "" ||
        new Date(item.dates[0]) <= new Date(endDateFilter));

    return (
      matchesLeaveType &&
      matchesLeaveStatus &&
      matchesDateRange
    );
  });

  const normalizeLeaveStatus = (status) =>
    status?.toUpperCase?.() === "REVOKE" ? "REVOKE" : "REQUESTED";

  // Toggle individual selection
  const handleToggleSelect = (leaveId) => {
    setSelectedLeaves((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(leaveId)) {
        newSet.delete(leaveId);
      } else {
        newSet.add(leaveId);
      }
      return newSet;
    });
  };

  // Toggle select all
  const handleSelectAll = () => {
    if (selectedLeaves.size === data.length) {
      setSelectedLeaves(new Set());
    } else {
      setSelectedLeaves(new Set(data.map((item) => item.leaveId)));
    }
  };

  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedLeaves.size === 0) {
      toast.warning("Please select at least one leave request");
      return;
    }

    const payload = data
      .filter((item) => selectedLeaves.has(item.leaveId))
      .map((item) => ({
        leaveId: item.leaveId,
        leaveStatus: normalizeLeaveStatus(item.leaveStatus), // REQUESTED / REVOKE
      }));

    const baseUrl =
      activeRole === "HR"
        ? process.env.REACT_APP_API_HR_URL
        : process.env.REACT_APP_API_MANAGER_URL;

    try {
      await axiosInstance.put(`${baseUrl}/leave-history/approve/bulk`, payload);

      setData((prev) =>
        prev.filter((item) => !selectedLeaves.has(item.leaveId)),
      );
      setSelectedLeaves(new Set());

      toast.success(`Approved ${payload.length} leave requests`);
      onRequestProcessed?.();
    } catch (error) {
      console.error(error);
      toast.error("Bulk approve failed");
    }
  };

  // Bulk reject
  const handleBulkReject = async () => {
    if (selectedLeaves.size === 0) {
      toast.warning("Please select at least one leave request");
      return;
    }

    const selectedItems = data.filter((item) =>
      selectedLeaves.has(item.leaveId),
    );

    const revokePayload = selectedItems
      .filter((item) => normalizeLeaveStatus(item.leaveStatus) === "REVOKE")
      .map((item) => ({
        leaveId: item.leaveId,
        leaveStatus: "REQUESTED",
      }));

    const normalPayload = selectedItems
      .filter((item) => normalizeLeaveStatus(item.leaveStatus) !== "REVOKE")
      .map((item) => ({
        leaveId: item.leaveId,
        leaveStatus: "REQUESTED",
      }));

    const baseUrl =
      activeRole === "HR"
        ? process.env.REACT_APP_API_HR_URL
        : process.env.REACT_APP_API_MANAGER_URL;

    try {
      if (normalPayload.length > 0) {
        await axiosInstance.put(
          `${baseUrl}/leave-history/reject/bulk`,
          normalPayload,
        );
      }

      if (revokePayload.length > 0) {
        await axiosInstance.put(
          `${baseUrl}/leave-history/reject-revoke/bulk`,
          revokePayload,
        );
      }

      setData((prev) =>
        prev.filter((item) => !selectedLeaves.has(item.leaveId)),
      );
      setSelectedLeaves(new Set());

      toast.success(
        `Rejected ${selectedItems.length} leave request${
          selectedItems.length > 1 ? "s" : ""
        }`,
      );

      onRequestProcessed?.();
    } catch (error) {
      console.error(error);
      toast.error("Bulk reject failed");
    }
  };

  const handleApprove = async (leaveId, leaveStatus) => {
    const normalizedStatus = normalizeLeaveStatus(leaveStatus);
    const baseUrl =
      activeRole === "HR"
        ? process.env.REACT_APP_API_HR_URL
        : process.env.REACT_APP_API_MANAGER_URL;

    const endpoint =
      normalizedStatus === "REVOKE"
        ? `${baseUrl}/leave-history/revoke`
        : `${baseUrl}/leave-history/approve`;

    try {
      const response = await axiosInstance.put(endpoint, {
        leaveId,
        leaveStatus: "REQUESTED",
      });

      if (response.data.status === 200) {
        setData((prev) => prev.filter((item) => item.leaveId !== leaveId));

        toast.success(
          normalizedStatus === "REVOKE"
            ? "Leave revoke approved"
            : "Leave approved",
        );

        onRequestProcessed?.();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to approve leave");
    }
  };

  const handleReject = async (leaveId, leaveStatus) => {
    const normalizedStatus = normalizeLeaveStatus(leaveStatus);
    const baseUrl =
      activeRole === "HR"
        ? process.env.REACT_APP_API_HR_URL
        : process.env.REACT_APP_API_MANAGER_URL;

    const endpoint =
      normalizedStatus === "REVOKE"
        ? `${baseUrl}/leave-history/reject-revoke`
        : `${baseUrl}/leave-history/reject`;

    try {
      const response = await axiosInstance.put(endpoint, {
        leaveId,
        leaveStatus: "REQUESTED",
      });

      if (response.data.status === 200) {
        setData((prev) => prev.filter((item) => item.leaveId !== leaveId));

        toast.success(
          normalizedStatus === "REVOKE"
            ? "Leave revoke rejected"
            : "Leave rejected",
        );

        onRequestProcessed?.();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to reject leave");
    }
  };

  const formatDateRangeWithTooltip = (dates) => {
    if (!dates || dates.length === 0) return "—";

    const sortedDates = [...dates].sort((a, b) => new Date(a) - new Date(b));
    const start = new Date(sortedDates[0]);
    const end = new Date(sortedDates[sortedDates.length - 1]);

    const formatWithSuffix = (day) => {
      if (day > 3 && day < 21) return `${day}th`;
      switch (day % 10) {
        case 1:
          return `${day}st`;
        case 2:
          return `${day}nd`;
        case 3:
          return `${day}rd`;
        default:
          return `${day}th`;
      }
    };

    const range =
      start.toDateString() === end.toDateString()
        ? `${formatWithSuffix(start.getDate())} ${start.toLocaleString(
            "default",
            { month: "short" },
          )} ${start.getFullYear()}`
        : `${formatWithSuffix(start.getDate())} - ${formatWithSuffix(
            end.getDate(),
          )} ${start.toLocaleString("default", {
            month: "long",
          })} ${start.getFullYear()}`;

    return <span title={sortedDates.join(", ")}>{range}</span>;
  };

  const formatDepartment = (departmentString) => {
    if (!departmentString) return "—";

    const departments = departmentString
      .split(",")
      .map((d) => d.trim())
      .filter((d) => d);

    if (departments.length === 0) return "—";

    if (departments.length <= 2) {
      return departments.join(", ");
    }

    return (
      <span title={departments.join(", ")}>
        {departments[0]}, {departments[1]} +{departments.length - 2} more
      </span>
    );
  };

  // Helper function to format fallback status
  const formatFallbackStatus = (status) => {
    if (!status) return "—";

    const statusMap = {
      ACCEPTED: "Accepted",
      PENDING: "Yet to Accept",
      YET_TO_ACCEPT: "Yet to Accept",
    };

    return statusMap[status.toUpperCase()] || status;
  };

  const columns = [
    ...(canPerformActions() ? [{ header: "Select", accessor: "select" }] : []),
    { header: "Employee", accessor: "employeeName" },
    { header: "Role", accessor: "role" },
    { header: "Department", accessor: "department" },
    { header: "Leave Type", accessor: "leaveType" },
    { header: "Leave Period", accessor: "leavePeriod" },
    { header: "Reason", accessor: "reason" },
    { header: "Fallback Employee", accessor: "fallbackEmployeeName" },
    { header: "Fallback Status", accessor: "leaveFallbackStatus" },
    { header: "Responsibility", accessor: "responsibiltiy" },
    { header: "Leave Status", accessor: "leaveStatus" },
    ...(canPerformActions()
      ? [{ header: "Actions", accessor: "actions" }]
      : []),
  ];

  const tableData = data.map((row) => ({
    searchableText: [
      row.employeeName,
      row.role,
      row.department,
      row.leaveType,
      row.dates?.join(" "),
      row.reason,
      row.fallbackEmployeeName,
      row.leaveFallbackStatus,
      row.responsibiltiy,
      row.leaveStatus,
    ]
      .filter(Boolean)
      .join(" "),
    ...(canPerformActions() && {
      select: (
        <input
          type="checkbox"
          checked={selectedLeaves.has(row.leaveId)}
          onChange={() => handleToggleSelect(row.leaveId)}
          style={{ cursor: "pointer", width: "18px", height: "18px" }}
        />
      ),
    }),

    employeeName: row.employeeName || "—",
    role: row.role || "—",
    department: formatDepartment(row.department),
    leaveType: row.leaveType || "—",
    leavePeriod: formatDateRangeWithTooltip(row.dates),
    reason: row.reason ? (
      <span title={row.reason}>
        {row.reason.length > 40
          ? row.reason.substring(0, 40) + "..."
          : row.reason}
      </span>
    ) : (
      "—"
    ),
    fallbackEmployeeName: row.fallbackEmployeeName || "—",
    leaveFallbackStatus: formatFallbackStatus(row.leaveFallbackStatus),
    responsibiltiy: row.responsibiltiy || "—",
    leaveStatus: <StatusCard status={row.leaveStatus} />,

    ...(canPerformActions() && {
      actions: (
        <div className="action-buttons">
          {["REQUESTED", "REVOKE"].includes(row.leaveStatus?.toUpperCase()) && (
            <>
              <IconButton
                className="icon-button--add"
                icon="mdi:check"
                onClick={() => handleApprove(row.leaveId, row.leaveStatus)}
              >
                Approve
              </IconButton>

              <IconButton
                className="icon-button--delete"
                icon="mdi:close"
                onClick={() => handleReject(row.leaveId, row.leaveStatus)}
              >
                Reject
              </IconButton>
            </>
          )}
        </div>
      ),
    }),
  }));

  return (
    <div className="leave-report-container">
      <h1 className="display6">Manage Team Leave</h1>

      {viewReport && (
        <TeamLeaveReport
          onClose={() => setViewReport(false)}
          leaveData={filteredData}
        />
      )}
      {viewSummary && <LeaveBalanceReport />}

      <div className="leave-report-filters-inputs">
        <div className="filter-row">
          <div className="filter-items">
            <InputField
              className="leave-report-input"
              id="startDateFilter"
              type="date"
              label="From date"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
            />
          </div>
          <div className="filter-items">
            <InputField
              className="leave-report-input"
              id="endDateFilter"
              type="date"
              label="To date"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-items">
            <label>Leave Status</label>
            <div className="leave-report-filter-buttons">
              {["All", "Requests", "Revoke Requests"].map((status) => (
                <button
                  key={status}
                  className={`leave-report-filter-button ${
                    leaveStatusFilter === status ? "active" : ""
                  }`}
                  onClick={() => setLeaveStatusFilter(status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {canPerformActions() && data.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
            alignItems: "center",
          }}
        >
          <button
            onClick={handleSelectAll}
            style={{
              padding: "8px 16px",
              backgroundColor:
                selectedLeaves.size === data.length ? "#e0e0e0" : "#f5f5f5",
              border: "1px solid #ccc",
              borderRadius: "20px",
              cursor: "pointer",
              fontSize: "14px",
              marginTop: "10px",
            }}
          >
            {selectedLeaves.size === data.length
              ? "Deselect All"
              : "Select All"}
          </button>

          {selectedLeaves.size > 0 && (
            <>
              <span style={{ color: "#666", fontSize: "14px" }}>
                {selectedLeaves.size} selected
              </span>
              <IconButton
                className="icon-button--add"
                icon="mdi:check-all"
                onClick={handleBulkApprove}
                style={{ marginLeft: "auto" }}
              >
                Approve Selected ({selectedLeaves.size})
              </IconButton>

              <IconButton
                className="icon-button--delete"
                icon="mdi:close-thick"
                onClick={handleBulkReject}
              >
                Reject Selected ({selectedLeaves.size})
              </IconButton>
            </>
          )}
        </div>
      )}

      <GeneralTable
        columns={columns}
        data={tableData}
        title={`Manage Team Leave`}
        titleActions={
          <div className="team-page-toolbar-actions">
            <button
              className={`report-button ${viewReport ? "active" : ""}`}
              onClick={() => setViewReport(!viewReport)}
            >
              {viewReport ? "Close Report" : "Complete Report"}
            </button>
            <button
              className={`leave-balance-summary ${viewSummary ? "active" : ""}`}
              onClick={() => setViewSummary(!viewSummary)}
            >
              {viewSummary ? "Close Balance" : "Leave Balance"}
            </button>
            {showRoleButtons && !viewReport && (
              <div className="team-leave-role-buttons">
                {availableRoles.map((role) => (
                  <button
                    key={role}
                    className={`team-leave-role-btn ${activeRole === role ? "active" : ""}`}
                    onClick={() => handleRoleChange(role)}
                  >
                    {role}
                  </button>
                ))}
              </div>
            )}
          </div>
        }
      />
    </div>
  );
}

export default LeaveReport;
